/**
 * U0 · what a step shows and refers to: images, documents, procedures.
 *
 * An image or document is content of the step (uploaded, hashed, printed with the step). A
 * procedure is a link into the SOP library — the library owns the document and its versions;
 * the step says which procedure it is performed under. Both are picked or uploaded here, never
 * typed: a typed SOP number is one the library cannot resolve.
 */
import React, { useState } from "react";
import { Button, Flex, Input, Popconfirm, Select, Tooltip, Typography, Upload } from "antd";
import { DeleteOutlined, FileTextOutlined, LinkOutlined, PictureOutlined, UploadOutlined } from "@ant-design/icons";

const { Text } = Typography;

export default function StepAttachments({
  step, readOnly = false, limits = {},
  onUpload,            // (file, {kind, caption})
  onRemoveAttachment,  // (attachmentId)
  onLinkSop,           // ({sop_id, context})
  onUnlinkSop,         // (linkId, reason)
  searchSops,          // async (q) → [{id, sop_no, title, version}]
}) {
  // F9: what the type's source step had attached, as evidence this step is expected to
  // carry — the file itself stayed with the record it was taken for.
  const expected = step.payload?.expected_attachments || step.expected_attachments || [];
  const images = (step.attachments || []).filter((a) => a.kind === "IMAGE");
  const docs = (step.attachments || []).filter((a) => a.kind !== "IMAGE");
  const sops = step.sops || [];
  const [caption, setCaption] = useState("");
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const types = limits.attachment_types || {};
  const accept = (kind) => (types[kind] || []).map((e) => `.${e}`).join(",");

  const search = async (q) => {
    if (!searchSops) return;
    setSearching(true);
    try {
      const rows = await searchSops(q);
      setOptions((rows || []).map((s) => ({ value: s.id, label: `${s.sop_no} · ${s.title}${s.version ? ` · v${s.version}` : ""}` })));
    } finally { setSearching(false); }
  };

  return (
    <Flex vertical gap={10}>
      {expected.length ? (
        <Flex vertical gap={4} className="u0-expected">
          <span className="dz-eyebrow">Expected here — attached on the record this type came from</span>
          {expected.map((a, i) => (
            <div key={i} className="dz-kv"><span className="k">{a.kind === "IMAGE" ? "image" : "document"}</span>
              <span className="v">{a.caption || a.name}{a.caption && a.name ? <span style={{ opacity: .7 }}> · {a.name}</span> : null}</span></div>
          ))}
          <Text type="secondary" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>The file stayed with that record; attach this record's own.</Text>
        </Flex>
      ) : null}
      <Flex vertical gap={6}>
        <span className="dz-eyebrow">Images — what the operator is shown</span>
        {images.length ? (
          <div className="u0-imgs">
            {images.map((a) => (
              <figure key={a.id} className="u0-img">
                <img src={a.url} alt={a.caption || a.name} />
                <figcaption>
                  <span>{a.caption || a.name}</span>
                  {!readOnly && onRemoveAttachment ? (
                    <Popconfirm title="Remove this image from the step?" onConfirm={() => onRemoveAttachment(a.id)}>
                      <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label="Remove image" />
                    </Popconfirm>
                  ) : null}
                </figcaption>
              </figure>
            ))}
          </div>
        ) : <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>No image on this step.</Text>}
        {!readOnly && onUpload ? (
          <Flex gap={6} align="center" wrap>
            <Input size="small" placeholder="Caption — what the picture shows" value={caption}
                   onChange={(e) => setCaption(e.target.value)} style={{ flex: 1, minWidth: 180 }} />
            <Upload accept={accept("IMAGE")} showUploadList={false}
                    customRequest={({ file, onSuccess }) => { onUpload(file, { kind: "IMAGE", caption }); setCaption(""); onSuccess?.({}); }}>
              <Button size="small" icon={<PictureOutlined />}>Add image</Button>
            </Upload>
            <Upload accept={accept("DOCUMENT")} showUploadList={false}
                    customRequest={({ file, onSuccess }) => { onUpload(file, { kind: "DOCUMENT", caption }); setCaption(""); onSuccess?.({}); }}>
              <Button size="small" icon={<UploadOutlined />}>Add document</Button>
            </Upload>
          </Flex>
        ) : null}
        {docs.length ? docs.map((a) => (
          <div key={a.id} className="u0-doc">
            <FileTextOutlined />
            <a href={a.url} target="_blank" rel="noreferrer">{a.caption || a.name}</a>
            <span className="dz-code" title="SHA-256 recorded at upload">{String(a.sha256 || "").slice(0, 10)}</span>
            {!readOnly && onRemoveAttachment ? (
              <Popconfirm title="Remove this document from the step?" onConfirm={() => onRemoveAttachment(a.id)}>
                <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label="Remove document" />
              </Popconfirm>
            ) : null}
          </div>
        )) : null}
      </Flex>

      <Flex vertical gap={6}>
        <span className="dz-eyebrow">Procedures — the SOPs this step is performed under</span>
        {sops.length ? sops.map((s) => (
          <div key={s.link_id} className="u0-doc">
            <LinkOutlined />
            {s.url ? <a href={s.url} target="_blank" rel="noreferrer">{s.sop_no}</a> : <b>{s.sop_no}</b>}
            <span style={{ flex: 1, minWidth: 0 }}>{s.title}</span>
            <span className="dz-chip">v{s.version} · {s.context}</span>
            {!readOnly && onUnlinkSop ? (
              <Tooltip title="Unlink — the library keeps the document; the step stops referring to it">
                <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label="Unlink procedure"
                        onClick={() => onUnlinkSop(s.link_id, "")} />
              </Tooltip>
            ) : null}
          </div>
        )) : <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>No procedure linked.</Text>}
        {!readOnly && onLinkSop ? (
          <Select size="small" showSearch filterOption={false} loading={searching} options={options}
                  placeholder="Find an effective SOP by number or title" value={null}
                  onSearch={search} onFocus={() => !options.length && search("")}
                  onChange={(id) => id && onLinkSop({ sop_id: id, context: "EXECUTION" })} />
        ) : null}
      </Flex>
    </Flex>
  );
}
